import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigConstants,
    ConfigService,
    CONNECTION_ERROR_CODES,
    LoadingScreenComponent,
    LoadingScreenModel,
    SynchronizationInstanceToken,
    SynchronizationInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, EMPTY, from, of, timer } from 'rxjs';
import {
    catchError,
    concatMap,
    debounceTime,
    exhaustMap,
    filter,
    finalize,
    first,
    groupBy,
    map,
    mergeMap,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { FormsStorageService } from '../../services/forms-storage.service';
import { FormsWriteInstanceToken, FormsWriteInterface } from '../../services/forms-write.interface';
import { FormsService } from '../../services/forms.service';
import { FormsConstants } from '../../types/forms-constants';
import { FormUtilities } from '../../utilities/form-utilities';
import { emptyAction } from '../actions/empty.actions';
import { loadFormList } from '../actions/form-list.actions';
import {
    abortForm,
    abortFormSuccess,
    afterSubmitFormSuccess,
    generateChecklistCopiesSuccess,
    markFormAsEdited,
    refreshXML,
    refreshXMLSuccess,
    saveForm,
    saveFormSuccess,
    submitForm,
    submitFormError,
    submitFormSuccess,
} from '../actions/form.actions';
import {
    deleteLastOpenedForm,
    saveLastOpenedFormSuccess,
    updateLastOpenedForm,
} from '../actions/last-opened-form.actions';
import { submitSpqDocSuccess } from '../actions/questionnaire.actions';
import { getForm } from '../selectors/form.selector';
import { getLastOpenedForm } from '../selectors/last-opened-form.selector';
import { getFormListEntryForNotificationFactory, getFormLockFromFormListEntry } from '../selectors/list.selector';
import { FormsState } from '../state/pace-forms.state';
import { BroadcastService } from '../../services/broadcast.service';
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;

// Effects taking care of saving/submitting and aborting of forms
@Injectable()
export class FormSaveEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store<FormsState>,
        private readonly formsService: FormsService,
        private readonly modalService: BsModalService,
        private readonly router: Router,
        public readonly configService: ConfigService,
        private readonly formsStorageService: FormsStorageService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        private readonly broadcastService: BroadcastService,
        @Inject(FormsWriteInstanceToken) private readonly formsWriteService: FormsWriteInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(SynchronizationInstanceToken) private readonly synchronizationService: SynchronizationInterface,
    ) {}

    public saveFormOnEdit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(markFormAsEdited),
            debounceTime(ConfigConstants.debounceTimeMs),
            switchMap(() => of(saveForm())),
        ),
    );

    public saveForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveForm, generateChecklistCopiesSuccess),
            withLatestFrom(this.store.select(getForm)),
            filter(([, form]) => form.formId.formId !== '' && form.lockedBy === this.configService.getUserFrontId()),
            mergeMap(([, form]) =>
                this.formsService
                    .save(form)
                    .pipe(map(() => saveFormSuccess({ form: form.formId, notification: form.notification }))),
            ),
            catchError((error) => {
                this.log.error(`FormEffects: saveForm$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public saveFormSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(saveFormSuccess),
                tap((form) => {
                    this.broadcastService.sendMessage({
                        saveStatus: FormsConstants.SUBMIT_STATUS.EDITED,
                        formId: form.form.formId,
                        notificationNumber: form.notification.qmNum,
                    });
                }),
                catchError((error) => {
                    this.log.error(`FormEffects: saveFormSuccess$: error: ${JSON.stringify(error)}`);
                    return EMPTY;
                }),
            ),
        { dispatch: false },
    );

    public refreshXML$ = createEffect(() =>
        this.actions$.pipe(
            ofType(refreshXML),
            switchMap((action) => {
                const { form, mainEquipment, notification, fromCache } = action;
                const loadingScreenModel = new LoadingScreenModel('');
                const loadingModalInstance = this.modalService.show(LoadingScreenComponent, {
                    backdrop: 'static',
                    class: 'modal-sm',
                    initialState: {
                        model: loadingScreenModel,
                    },
                    ariaLabelledBy: 'loading-screen-modal-title',
                });
                return this.formsService.refreshXML(form).pipe(
                    mergeMap(() =>
                        combineLatest([
                            this.formsStorageService.removeLockFromCachedOdataList(form, notification),
                            this.formsStorageService.removeFormStateFromCache(form, notification),
                            this.formsStorageService.removeLockedByFromFormXmlInCache(form, notification).pipe(
                                catchError((error) => {
                                    this.log.warn(
                                        `ProtocolsEffects: refreshXML$: could not remove lock from form XML in cache, probably there is no cache. Proceeding anyway: ${JSON.stringify(
                                            error,
                                        )}`,
                                    );
                                    return of({});
                                }),
                            ),
                        ]),
                    ),
                    // remove the locked by field from the forms list in local store and update the status
                    map(() =>
                        refreshXMLSuccess({
                            form,
                            mainEquipment,
                            notification,
                            fromCache,
                        }),
                    ),
                    finalize(() => {
                        loadingModalInstance.hide();
                    }),
                    catchError((error) => {
                        this.log.error(
                            `ProtocolsEffects: refreshXML$: error in refreshing XML of form: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return EMPTY;
                    }),
                );
            }),
        ),
    );

    public refreshXMLSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(refreshXMLSuccess),
            switchMap((action) => {
                const { form, mainEquipment, notification, fromCache } = action;
                return this.formsStorageService.saveFormSubmitStatus(notification, form, SUBMIT_STATUS.REFRESHED).pipe(
                    mergeMap(() =>
                        of(
                            loadFormList({
                                mainEquipment,
                                equipmentNr: form.equipmentId,
                                notification,
                                fromCache,
                            }),
                        ),
                    ),
                    catchError((error) => {
                        this.log.error(
                            `ProtocolsEffects: refreshXMLSuccess$: error in pipeline: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return EMPTY;
                    }),
                );
            }),
        ),
    );

    public abortForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(abortForm),
            groupBy(
                (action) => action.notification.maNum + action.notification.qmNum,
                undefined,
                // abortForm actions are synchronously emmited and timer will complete group asynchronously on next loop
                // abortForm can be triggered once or multiple times for all forms at once (synchronously)
                (group$) => group$.pipe(exhaustMap(() => timer(0))),
            ),
            mergeMap((group$) => {
                const loadingScreenModel = new LoadingScreenModel('');
                const modalInstance = this.modalService.show(LoadingScreenComponent, {
                    backdrop: 'static',
                    class: 'modal-sm',
                    initialState: {
                        model: loadingScreenModel,
                    },
                    ariaLabelledBy: 'loading-screen-modal-title',
                });
                return group$.pipe(
                    mergeMap((action) =>
                        combineLatest([
                            this.formsStorageService.removeLockFromCachedOdataList(action.form, action.notification),
                            this.formsStorageService.removeFormStateFromCache(action.form, action.notification),
                            this.store
                                .select(
                                    getFormLockFromFormListEntry({
                                        formId: action.form,
                                        notification: {
                                            frontId: action.notification.frontId,
                                            maNum: action.notification.maNum,
                                            qmNum: action.notification.qmNum,
                                        },
                                    }),
                                )
                                .pipe(first()),
                        ]).pipe(
                            first(),
                            mergeMap(([, , lockedBy]) => {
                                this.log.info(
                                    `FormEffects: abortForm$: User '${this.configService.getUserFrontId()}' is trying to abort form locked by '${lockedBy}'`,
                                );
                                if (lockedBy === '' || lockedBy === this.configService.getUserFrontId()) {
                                    // only call releaseLockSet in gateway when the entry is actually locked by the user
                                    // otherwise just remove the lock locally so the user can continue working
                                    // relevant for SHP-833
                                    return this.formsWriteService.releaseLock(action.form);
                                } else {
                                    this.log.info(
                                        'FormEffects: abortForm$: Won`t abort form as it is not locked to user.',
                                    );
                                    return of(undefined);
                                }
                            }),
                            mergeMap(() => {
                                // Only removing the lock from the cached XML, but not the entire cache yet.
                                // There might be other notifications for the same equipment that still might need the cache.
                                // When submitting the confirmation, the forms service takes care of removing all cached files
                                // for that notification that are no longer needed, including the cached XML if there are no
                                // other notifications relying on it any more
                                return this.formsStorageService
                                    .removeLockedByFromFormXmlInCache(action.form, action.notification)
                                    .pipe(
                                        catchError((error) => {
                                            this.log.warn(
                                                `FormEffects: abortForm$: could not remove lock from form XML in cache. Proceeding anyway: ${JSON.stringify(
                                                    error,
                                                )}`,
                                            );
                                            return of({});
                                        }),
                                    );
                            }),
                            // Once release lock call was successful, update the stored local copy of form list and remove
                            // the lock there as well
                            map(() => {
                                return abortFormSuccess({
                                    form: action.form,
                                    notification: action.notification,
                                    navigateToDetails: action.navigateToDetails ?? true,
                                });
                            }),
                        ),
                    ),
                    finalize(() => {
                        modalInstance.hide();
                    }),
                );
            }),
            catchError((error) => {
                this.log.error(`FormEffects: abortForm$: error in pipeline: ${JSON.stringify(error)}`);
                if (error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR) {
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE'),
                        <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY'),
                    );
                }

                return EMPTY;
            }),
        ),
    );

    public abortFormSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(abortFormSuccess),
            concatMap((action) =>
                this.store
                    .select(
                        getFormListEntryForNotificationFactory({
                            formId: action.form,
                            notification: action.notification,
                        }),
                    )
                    .pipe(
                        take(1),
                        mergeMap((formListEntry) => {
                            if (!formListEntry?.id) {
                                return of(undefined);
                            }
                            return this.formsStorageService.saveFormSubmitStatus(
                                action.notification,
                                formListEntry.id,
                                formListEntry?.submitStatus ?? SUBMIT_STATUS.UNKNOWN,
                            );
                        }),
                        map(() => action),
                    ),
            ),
            tap((action) =>
                this.broadcastService.sendMessage({
                    saveStatus: FormsConstants.SUBMIT_STATUS.ABORTED,
                    formId: action.form.formId,
                    notificationNumber: action.notification.qmNum,
                }),
            ),
            mergeMap((action) => {
                if (action.navigateToDetails) {
                    return from(
                        this.router.navigate([
                            ConfigConstants.routeUrlDetails,
                            action.notification.frontId,
                            action.notification.qmNum,
                            action.notification.maNum,
                        ]),
                    );
                } else {
                    return EMPTY;
                }
            }),
            map(() => emptyAction()),
            catchError((error) => {
                this.log.error(`FormEffects: abortFormSuccess$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public submitForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitForm),
            withLatestFrom(this.store.select(getForm)),
            mergeMap(([action, form]) => {
                const loadingScreenModel = new LoadingScreenModel('');
                const modalInstance = this.modalService.show(LoadingScreenComponent, {
                    backdrop: 'static',
                    class: 'modal-sm',
                    initialState: {
                        model: loadingScreenModel,
                    },
                    ariaLabelledBy: 'loading-screen-modal-title',
                });
                const skipFormListProcessing =
                    this.configService.getEnableSafetyAssessmentForm() &&
                    FormUtilities.isSafetyAssessmentForm(
                        form.formId.formId,
                        this.configService.getSafetyAssessmentForm(),
                    );

                return loadingScreenModel.loadingScreenDisplayed$.pipe(
                    mergeMap(() => {
                        if (action.id === FormsConstants.MASTER_PARTICIPANT_ID) {
                            const participantCopies = Object.values(form.participantCopies)
                                // remove the master from participant copy array
                                .filter(
                                    (participantCopy) => participantCopy.id !== FormsConstants.MASTER_PARTICIPANT_ID,
                                )
                                // remove all copies that were already submitted previously
                                .filter((participantCopy) => participantCopy.submittedOn.length === 0)
                                .map((participantCopy) => participantCopy.id);

                            return this.formsService
                                .submitParticipantCopies(form, participantCopies)
                                .pipe(mergeMap(() => this.formsService.submitMaster(form, action.saveMode)));
                        } else {
                            return this.formsService.submitParticipantCopies(form, [action.id]);
                        }
                    }),
                    mergeMap(() => this.synchronizationService.sendOfflineData()),
                    map(() => {
                        return submitFormSuccess({
                            ids: [action.id],
                            form: form.formId,
                            notification: form.notification,
                            saveMode: action.saveMode,
                            skipFormListProcessing,
                        });
                    }),
                    catchError((error: Error) => {
                        this.log.error(`FormEffects: submitForm$: error in pipeline: ${JSON.stringify(error)}`);
                        // if the error is because of synchronization issue
                        // it means submitted form data is already saved in the offline store
                        // and can be treated as success
                        if (error.message === CONNECTION_ERROR_CODES.OFFLINE_PLUGIN_SYNC_ERROR) {
                            return of(
                                submitFormSuccess({
                                    ids: [action.id],
                                    form: form.formId,
                                    notification: form.notification,
                                    saveMode: action.saveMode,
                                    skipFormListProcessing,
                                }),
                            );
                        } else {
                            return from([
                                saveForm(),
                                submitFormError({
                                    id: action.id,
                                    saveMode: action.saveMode,
                                    form: form.formId,
                                    notification: form.notification,
                                }),
                            ]);
                        }
                    }),
                    tap(() => modalInstance.hide()),
                );
            }),
        ),
    );

    public submitFormError$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(submitFormError),
                tap(() => {
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('FORM_SUBMISSION_ERROR'),
                        <string>this.translate.instant('FORM_SUBMISSION_ERROR_BODY'),
                    );
                }),
            ),
        { dispatch: false },
    );

    public submitFormSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitFormSuccess),
            withLatestFrom(this.store.select(getForm)),
            mergeMap(([action, form]) =>
                this.formsService.save(form).pipe(map((savedForm) => ({ savedForm, action }))),
            ),
            mergeMap(({ savedForm, action }) => {
                const enableUnlimitedHandover = this.configService.getEnableUnlimitedHandover();

                this.broadcastService.sendMessage({
                    saveStatus: action.saveMode,
                    formId: savedForm.formId.formId,
                    notificationNumber: savedForm.notification.qmNum,
                });

                if (enableUnlimitedHandover && action.saveMode === FormsConstants.SUBMIT_STATUS.HANDOVER) {
                    return this.formsService.removeFormFromCacheCompletely(action.form, action.notification).pipe(
                        map(() => savedForm),
                        catchError((err) => {
                            this.log.error(
                                `FormSaveEffects: submitFormSuccess$: could not remove form from cache: ${JSON.stringify(err)}`,
                            );
                            return of(savedForm);
                        }),
                    );
                }

                return of(savedForm);
            }),
            mergeMap((savedForm) => {
                if (FormUtilities.isServicePartQuestionnaire(savedForm.type)) {
                    return from(
                        this.router.navigate([
                            FormsConstants.ROUTE_CONFIRMATION,
                            savedForm.notification.frontId,
                            savedForm.notification.qmNum,
                            savedForm.notification.maNum,
                            FormsConstants.CONFIRMATION_TYPE_NORMAL,
                        ]),
                    ).pipe(map(() => savedForm));
                }
                // perform navigation only if the submitted form is the "master" form
                if (savedForm.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID) {
                    return from(
                        this.router.navigate([
                            ConfigConstants.routeUrlDetails,
                            savedForm.notification.frontId,
                            savedForm.notification.qmNum,
                            savedForm.notification.maNum,
                        ]),
                    ).pipe(map(() => savedForm));
                } else {
                    return of(savedForm);
                }
            }),
            filter((form) => !FormUtilities.isServicePartQuestionnaire(form.type)),
            map((f) => afterSubmitFormSuccess({ form: f.formId, notification: f.notification })),
            catchError((error) => {
                this.log.error(`FormEffects: submitFormSuccess$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public saveLastOpenedForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateLastOpenedForm, deleteLastOpenedForm),
            withLatestFrom(this.store.select(getLastOpenedForm)),
            mergeMap(([, lastOpenedForm]) => this.formsService.saveLastOpenedForm(lastOpenedForm)),
            map(() => saveLastOpenedFormSuccess()),
            catchError((error) => {
                this.log.error(`FormEffects: saveLastOpenedForm$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public submitSpqDocSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitSpqDocSuccess),
            withLatestFrom(this.store.select(getForm)),
            switchMap(([, form]) =>
                this.formsService.save(form).pipe(
                    mergeMap(() => {
                        return from(
                            this.router.navigate([
                                FormsConstants.ROUTE_CONFIRMATION,
                                form.notification.frontId,
                                form.notification.qmNum,
                                form.notification.maNum,
                                FormsConstants.CONFIRMATION_TYPE_NORMAL,
                            ]),
                        ).pipe(map(() => form));
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEffects: submitSpqDocSuccess$: error in pipeline: ${JSON.stringify(error)}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );
}
